import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useAuth } from '../../hooks/useAuth';
import maintenanceService from '../../services/maintenanceService';

const Screen = styled.main`
  position: fixed;
  inset: 0;
  width: auto;
  height: 100vh;
  height: 100dvh;
  min-height: 0;
  margin: 0;
  box-sizing: border-box;
  overflow-y: auto;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  color: #e2e8f0;
  background: radial-gradient(1200px 600px at 20% 10%, #1f2937 0%, #0f172a 45%),
    radial-gradient(1000px 500px at 80% 80%, #0b1324 0%, #1e293b 55%);
  display: grid;
  place-items: center;
  padding: 24px;

  @media (max-width: 600px) {
    padding: 16px;
  }
`;

const Card = styled.section`
  width: min(720px, 96vw);
  box-sizing: border-box;
  padding: clamp(20px, 4vw, 32px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);

  h1 {
    margin: 16px 0 8px;
    font-size: clamp(28px, 5vw, 42px);
    line-height: 1.1;
  }

  p {
    margin: 8px 0 0;
    color: #94a3b8;
    font-size: 16px;
    line-height: 1.6;
  }

  @media (max-width: 600px) {
    width: 100%;
    border-radius: 14px;
    text-align: center;

    h1 {
      font-size: clamp(24px, 7vw, 34px);
    }

    p {
      font-size: 15px;
    }
  }
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border: 1px solid rgba(34, 197, 94, 0.35);
  border-radius: 999px;
  color: #a7f3d0;
  background: rgba(34, 197, 94, 0.16);
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const Meta = styled.div`
  margin-top: 18px;
  display: grid;
  gap: 8px;
  color: #94a3b8;
  font-size: 14px;

  strong {
    color: #e2e8f0;
    font-weight: 600;
  }

  @media (max-width: 600px) {
    font-size: 13px;
    justify-items: center;
  }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.65; }
  50% { opacity: 1; }
`;

const Progress = styled.div`
  margin-top: 20px;
  height: 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;

  @media (max-width: 600px) {
    height: 6px;
  }
`;

const ProgressBar = styled.span`
  display: block;
  width: 62%;
  height: 100%;
  background: linear-gradient(90deg, #22c55e, #16a34a);
  box-shadow: 0 0 18px rgba(34, 197, 94, 0.6);
  animation: ${pulse} 2.2s infinite ease-in-out;
`;

const Footer = styled.div`
  margin-top: 22px;
  color: #94a3b8;
  font-size: 12px;

  @media (max-width: 600px) {
    font-size: 11px;
  }
`;

const MaintenanceGuard = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [maintenance, setMaintenance] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    let active = true;

    const refreshStatus = async () => {
      try {
        const data = await maintenanceService.getStatus();
        if (active) setMaintenance(Boolean(data.maintenance));
      } catch (error) {
        // A status check failure should not make the entire frontend unavailable.
        console.error('Maintenance status check failed:', error.message);
      } finally {
        if (active) setChecked(true);
      }
    };

    refreshStatus();
    const interval = window.setInterval(refreshStatus, 30000);

    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, []);

  const roles = Array.isArray(user?.roles) ? user.roles : [];
  const isAdmin = roles.includes('ROLE_ADMIN') || roles.includes('ROLE_SUPER_ADMIN');

  if (!checked || authLoading) {
    return null;
  }

  const isAdminSignIn = !user && location.pathname === '/login';

  if (maintenance && !isAdmin && !isAdminSignIn) {
    return (
      <Screen>
        <Card role="status" aria-live="polite">
          <Badge>Maintenance</Badge>
          <h1>Maintenance en cours</h1>
          <p>Nous preparons la mise en production. Le service revient tres bientot.</p>
          <Meta><div><strong>Contact:</strong> contact@maclectic.com</div></Meta>
          <Progress aria-hidden="true"><ProgressBar /></Progress>
          <Footer>Merci pour votre patience.</Footer>
        </Card>
      </Screen>
    );
  }

  return children;
};

export default MaintenanceGuard;
